import { CATEGORIES, type Category, type Item, type ItemInput, type User } from '@closetly/core';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ItemSheet, type SaveTo, type SharedLink } from '../components/ItemSheet';
import { SaveSheet } from '../components/SaveSheet';
import { newRecordId } from './ids';
import type { SaveTarget } from './membership';
import { useBoards, useDeleteItem, useOutfits, useSaveBoard, useSaveItem, useSaveOutfit, willQueue } from './queries';
import { useToast } from './toast';

export interface NewItemOptions {
  shared?: SharedLink | null;
  category?: Category;
  /** Save the new piece into this outfit / onto this board right away (preselected, can be changed). */
  outfitId?: string;
  boardId?: string;
  /** Instead: hand the new piece's id to an outfit still being edited. */
  onCreated?: (id: string) => void;
}

interface ItemEditor {
  openNew: (opts?: NewItemOptions) => void;
  openItem: (item: Item) => void;
  toggleBought: (item: Item) => void;
  /** "Merken": put a piece or an outfit on boards (and a piece into outfits). */
  openSave: (target: SaveTarget) => void;
}

const EditorContext = createContext<ItemEditor | null>(null);

export function useItemEditor(): ItemEditor {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useItemEditor outside ItemEditorProvider');
  return ctx;
}

type Editing = { item: Item | null; opts: NewItemOptions } | null;

/**
 * Owns the item sheet and the "Merken" sheet for the whole app, so every page
 * adds, changes and sorts pieces the same way.
 */
export function ItemEditorProvider({ user, children }: { user: User; children: ReactNode }) {
  const [editing, setEditing] = useState<Editing>(null);
  const [saving, setSaving] = useState<SaveTarget | null>(null);
  const saveItem = useSaveItem(user.id);
  const deleteItem = useDeleteItem(user.id);
  const saveOutfit = useSaveOutfit(user.id);
  const saveBoard = useSaveBoard(user.id);
  const outfits = useOutfits(user.id).data ?? [];
  const boards = useBoards(user.id).data ?? [];
  const toast = useToast();

  const saveFailed = useCallback(() => toast('Speichern fehlgeschlagen. Versuch es nochmal.'), [toast]);
  // said instead of the usual confirmation while changes wait for the connection
  const queued = 'Gespeichert. Wird gesendet, sobald du wieder online bist.';

  const api = useMemo<ItemEditor>(
    () => ({
      openNew: (opts = {}) => setEditing({ item: null, opts }),
      openItem: (item) => setEditing({ item, opts: {} }),
      openSave: (target) => setSaving(target),
      toggleBought: (item) => {
        saveItem.mutate({ id: item.id, owner: user.id, data: { bought: !item.bought } }, { onError: saveFailed });
        toast(willQueue() ? queued : item.bought ? 'Wieder auf der Liste' : 'Als gekauft markiert');
      },
    }),
    [saveItem, user.id, saveFailed, toast],
  );

  function save(data: ItemInput, to: SaveTo) {
    if (!editing) return;
    const { item, opts } = editing;
    if (data.image instanceof File && willQueue()) {
      // files can't wait in the offline queue
      toast('Fotos lassen sich nur mit Verbindung hochladen.');
      return;
    }
    setEditing(null);
    if (item) {
      // only what the form changed: a price the server checked meanwhile stays
      const changes = Object.fromEntries(
        Object.entries(data).filter(([k, value]) => item[k as keyof Item] !== value),
      ) as Partial<ItemInput>;
      if (Object.keys(changes).length > 0)
        saveItem.mutate({ id: item.id, owner: user.id, data: changes }, { onError: saveFailed });
      toast(willQueue() ? queued : 'Änderungen gespeichert');
      return;
    }
    const id = newRecordId();
    saveItem.mutate({ createId: id, owner: user.id, data }, { onError: saveFailed });
    // all writes run in order, so the piece exists before it is saved anywhere
    opts.onCreated?.(id);
    for (const outfitId of to.outfits)
      saveOutfit.mutate({ id: outfitId, owner: user.id, addItem: id }, { onError: saveFailed });
    for (const boardId of to.boards)
      saveBoard.mutate({ id: boardId, owner: user.id, add: { items: [id] } }, { onError: saveFailed });

    const first = outfits.find((o) => o.id === to.outfits[0]) ?? boards.find((b) => b.id === to.boards[0]);
    toast(willQueue() ? queued : first ? `Zu „${first.name || 'Outfit'}“ hinzugefügt` : 'Zur Liste hinzugefügt');
  }

  function remove(item: Item) {
    if (!confirm(`„${item.name}“ löschen?`)) return;
    setEditing(null);
    deleteItem.mutate(
      { id: item.id, owner: user.id },
      { onError: () => toast('Löschen fehlgeschlagen. Versuch es nochmal.') },
    );
    toast(willQueue() ? queued : 'Gelöscht');
  }

  return (
    <EditorContext value={api}>
      {children}
      {editing && (
        <ItemSheet
          key={editing.item?.id ?? 'new'}
          item={editing.item}
          defaultCategory={editing.opts.category ?? CATEGORIES[0]}
          shared={editing.opts.shared}
          // existing pieces are sorted with "Merken"; a piece for an outfit being edited joins that one
          saveTo={
            editing.item || editing.opts.onCreated
              ? null
              : {
                  outfits,
                  boards,
                  outfitIds: editing.opts.outfitId ? [editing.opts.outfitId] : [],
                  boardIds: editing.opts.boardId ? [editing.opts.boardId] : [],
                }
          }
          onSave={save}
          onDelete={remove}
          onClose={() => setEditing(null)}
        />
      )}
      {saving && <SaveSheet user={user} target={saving} onClose={() => setSaving(null)} />}
    </EditorContext>
  );
}
